import { type AreaPlugin } from 'rete-area-plugin'
import { PipelineEditorStepNode } from './nodes.tsx'
import { type AreaExtra, type NodeProps, type Schemes } from './types.ts'

// Spacing between nodes
const SPACER = 100

/**
 * Finds the root nodes in the node map.
 * Root nodes are nodes that are not referenced as "nextStepsRule.possibleNextSteps" in any other node.
 * @param nodeMap Map of the nodes
 * @return An array of root nodes
 */
export const findRootNodes = (nodeMap: { [pipelineStepId: string]: NodeProps }): NodeProps[] => {
  const allNodeIds = new Set(Object.keys(nodeMap))
  const referencedNodeIds = new Set<string>()

  // Gather all node IDs that are referenced in possibleNextSteps
  for (const node of Object.values(nodeMap)) {
    if (!(node instanceof PipelineEditorStepNode)) continue
    const possibleNextSteps = node?.input?.step?.nextStepsRule?.possibleNextSteps || []
    for (const nextStepId of possibleNextSteps) {
      referencedNodeIds.add(nextStepId)
    }
  }

  return Array.from(allNodeIds)
    .filter(nodeId => !referencedNodeIds.has(nodeId))
    .map(rootNodeId => nodeMap[rootNodeId])
}

/**
 * Arranges all the nodes from left to right.
 * A unique challenge is that each node's size (width/height) is dynamic due to user input,
 * so we must account for that when arranging these nodes.
 * @param nodeMap Map of the nodes
 * @param area Rete Area to arrange the nodes in
 * @return number The number of graphs present
 */
export const arrangeNodes = async (
  nodeMap: { [pipelineStepId: string]: NodeProps },
  area: AreaPlugin<Schemes, AreaExtra>
): Promise<NodeProps[]> => {
  const visitedNodes: { [nodeId: string]: NodeProps } = {}
  const rootNodes: NodeProps[] = findRootNodes(nodeMap)

  const transform = area.area.transform
  const zoom = transform.k

  const arrangeNodeGraph = async (node: NodeProps, yIndex: number, xIndex: number) => {
    if (!node) return
    if (visitedNodes?.[node.id]) return
    if (!(node instanceof PipelineEditorStepNode)) return

    visitedNodes[node.id] = node
    node.x = xIndex
    node.y = yIndex

    const element = document.getElementById(node.id)
    let width = 1
    let height = 1
    if (element) {
      const rect = element.getBoundingClientRect()
      width = rect.width / zoom
      height = rect.height / zoom
      node.height = height
      node.width = width
    }

    await area.translate(node.id, { x: node.x, y: node.y })

    const possibleNextSteps = node?.input?.step?.nextStepsRule?.possibleNextSteps ?? []

    let newY = yIndex
    for (const pipelineStepId of possibleNextSteps) {
      await arrangeNodeGraph(nodeMap[pipelineStepId], newY, xIndex + width + SPACER)
      newY += height + SPACER
    }

    return node
  }

  let yIndex = 0
  for (const node of rootNodes) {
    const hasArranged = await arrangeNodeGraph(node, yIndex, 0)

    if (hasArranged) {
      const element = document.getElementById(node.id)

      if (element) {
        const rect = element.getBoundingClientRect()
        const height = rect.height
        yIndex += height + SPACER
      }
    }
  }
  return rootNodes
}
